// Real Fresh Earth / OED deployments, sourced from company decks & the UltraTech proposal.
// Metrics reflect figures stated in those documents.

const px = (id, w = 1400) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${w}`;

export const caseStudies = [
  {
    slug: "bhubaneswar-bmc-biomining",
    city: "Bhubaneswar Municipal Corporation",
    region: "Odisha",
    tag: "Bio-mining",
    headline: "Bio-mining 2.25 lakh tonnes of legacy waste — with zero odour complaints.",
    summary:
      "At the Bhubaneswar Municipal Corporation, OED enabled the bio-mining of 2,25,000 metric tonnes of legacy waste and now supports 600–700 tonnes of daily waste with zero odour complaints.",
    beforeImg: px("37076702"),
    afterImg: px("13888402"),
    metrics: [
      { k: "Legacy waste bio-mined", v: "2.25L MT", note: "metric tonnes" },
      { k: "Daily waste managed", v: "600–700 MT", note: "ongoing, per day" },
      { k: "Odour complaints", v: "Zero", note: "post-deployment" },
    ],
    challenge:
      "A vast legacy dumpsite had resisted 10–15 different chemical treatments. Slow decomposition and overpowering odour blocked excavation and drew sustained public and regulatory pressure.",
    approach:
      "More than 2.25 lakh litres of OED bio-culture were produced on-site and applied across the active faces over four months, re-seeding microbial activity and accelerating decomposition ahead of screening.",
    outcome:
      "Odour was controlled by 95% on day one and 100% within a week. Work estimated at 9–10 months was completed in four, and the site now runs day-to-day with no odour complaints.",
    quote:
      "Ten to fifteen chemicals had failed us. The biological approach cleared the site in a third of the projected time.",
    quoteBy: "Bhubaneswar Municipal Corporation",
    hi: {
      headline: "2.25 लाख टन पुराने अपशिष्ट की बायो-माइनिंग — शून्य गंध शिकायतों के साथ।",
      summary: "भुवनेश्वर नगर निगम में, OED ने 2,25,000 मीट्रिक टन पुराने अपशिष्ट की बायो-माइनिंग संभव की और अब शून्य गंध शिकायतों के साथ प्रतिदिन 600–700 टन अपशिष्ट का समर्थन करता है।",
    },
  },
  {
    slug: "ultratech-cement-rdf",
    city: "UltraTech Cement — Manikgarh & Rawan",
    region: "Maharashtra & Chhattisgarh",
    tag: "Cement · RDF",
    headline: "Neutralising RDF odour inside a working cement plant in minutes.",
    summary:
      "For UltraTech Cement, OED removed the foul smell from RDF (refuse-derived fuel) waste and neutralised methane, hydrogen sulphide and ammonia — improving air around the plant without disrupting operations.",
    beforeImg: px("15060364"),
    afterImg: px("18468425"),
    metrics: [
      { k: "Smell neutralised", v: "1–2 min", note: "on application" },
      { k: "Bio-culture supplied", v: "22,000 L", note: "Manikgarh + Rawan" },
      { k: "Toxic gases", v: "Neutralised", note: "CH₄ · H₂S · NH₃" },
    ],
    challenge:
      "Stored and handled RDF released persistent odour and toxic gases, affecting workers and the plant environment, with no scope to halt fuel operations.",
    approach:
      "OED was fogged and sprinkled across RDF storage and handling zones at roughly 1 litre per tonne, forming a protective bio-film that reduced odour intensity even for fresh incoming waste.",
    outcome:
      "Smell was neutralised within 1–2 minutes of application and, after a month of regular dosing, the atmosphere stayed largely free of harmful gases.",
    quote:
      "The odour dropped within minutes of spraying — and stayed down as we kept applying it.",
    quoteBy: "Plant Operations, UltraTech Cement",
    hi: {
      headline: "एक चालू सीमेंट संयंत्र में RDF गंध को मिनटों में निष्क्रिय करना।",
      summary: "UltraTech Cement के लिए, OED ने RDF (रिफ्यूज़-डिराइव्ड फ्यूल) अपशिष्ट की दुर्गंध हटाई और मीथेन, हाइड्रोजन सल्फाइड व अमोनिया को निष्क्रिय किया — परिचालन बाधित किए बिना संयंत्र की हवा बेहतर की।",
    },
  },
  {
    slug: "puri-legacy-landfill",
    city: "Puri Municipality",
    region: "Odisha",
    tag: "Legacy Landfill",
    headline: "100% odour removed in 15 minutes at a mixed legacy dumpsite.",
    summary:
      "One of the earliest OED deployments (2019). On a site of 80% non-biodegradable and 20% biodegradable waste, foul smell was removed completely within 15 minutes and organic volume fell 60%.",
    beforeImg: px("4454064"),
    afterImg: px("29277514"),
    metrics: [
      { k: "Foul smell removed", v: "100%", note: "within 15 minutes" },
      { k: "Organic volume", v: "-60%", note: "reduction" },
      { k: "In service since", v: "2019", note: "one of the first sites" },
    ],
    challenge:
      "A mixed-composition dumpsite produced intense odour affecting the surrounding town, with slow natural decomposition of the biodegradable fraction.",
    approach:
      "OED was applied across the heap and inspected on follow-up visits, targeting the biodegradable load driving the smell.",
    outcome:
      "Complete odour removal within 15 minutes at demonstration, followed by a 60% reduction in organic waste volume — establishing an early, repeatable proof point.",
    quote:
      "The smell was gone within fifteen minutes of the demonstration. That is what convinced us.",
    quoteBy: "Puri Municipality, Odisha",
    hi: {
      headline: "मिश्रित पुराने डंपसाइट पर 15 मिनट में 100% गंध निवारण।",
      summary: "शुरुआती OED तैनातियों में से एक (2019)। 80% गैर-जैव-निम्नीकरणीय व 20% जैव-निम्नीकरणीय अपशिष्ट वाले स्थल पर, 15 मिनट में दुर्गंध पूरी तरह हटी और जैविक मात्रा 60% घटी।",
    },
  },
  {
    slug: "godda-nagar-parishad",
    city: "Godda Nagar Parishad",
    region: "Jharkhand",
    tag: "Municipal",
    headline: "Five years of continuous odour control since 2020.",
    summary:
      "Working with Godda Nagar Parishad since July 2020, OED achieves foul-smell removal within 2–5 minutes of application and has sustained clean-air conditions for years.",
    beforeImg: px("5424849"),
    afterImg: px("33786747"),
    metrics: [
      { k: "Smell removal", v: "2–5 min", note: "per application" },
      { k: "In service since", v: "Jul 2020", note: "5+ years" },
      { k: "Chemicals needed", v: "None", note: "fully biological" },
    ],
    challenge:
      "Ongoing municipal waste generated recurring odour complaints that chemical fixes could not resolve durably.",
    approach:
      "A regular OED application routine was established with the municipal team, integrated into existing waste handling.",
    outcome:
      "Consistent odour removal within 2–5 minutes and a multi-year working relationship demonstrating durability at municipal scale.",
    quote:
      "Five years on, the routine still works. Two to five minutes and the smell is gone.",
    quoteBy: "Godda Nagar Parishad",
    hi: {
      headline: "2020 से पाँच वर्षों का निरंतर गंध नियंत्रण।",
      summary: "जुलाई 2020 से गोड्डा नगर परिषद के साथ कार्य करते हुए, OED आवेदन के 2–5 मिनट में दुर्गंध हटाता है और वर्षों से स्वच्छ-वायु स्थितियाँ बनाए रखे हुए है।",
    },
  },
  {
    slug: "chas-bokaro-dumpsite",
    city: "Chas Municipal Corporation",
    region: "Jharkhand · Bokaro",
    tag: "Legacy Landfill",
    headline: "Seven years of service at the Chas–Bokaro dumpsite.",
    summary:
      "Among the longest-running OED engagements — continuous service at the Chas–Bokaro dumpsite, keeping odour in check and supporting land recovery over seven years.",
    beforeImg: px("9861464"),
    afterImg: px("2673552"),
    metrics: [
      { k: "Continuous service", v: "7 years", note: "and counting" },
      { k: "Approx. treatment cost", v: "₹150 / MT", note: "biological" },
      { k: "Weight reduction", v: "up to 70%", note: "of treated waste" },
    ],
    challenge:
      "A long-standing dumpsite required durable, cost-effective odour and decomposition management rather than one-off interventions.",
    approach:
      "On-site bio-culture production kept costs low (~₹150 per tonne), with sustained application across the site over years.",
    outcome:
      "A treated tonne of waste reduces to roughly 300 kg, and recovered material can be sold — turning a cost centre into a modest revenue stream while keeping odour controlled.",
    quote:
      "Seven years of a working relationship says more than any single trial could.",
    quoteBy: "Chas Municipal Corporation",
    hi: {
      headline: "चास–बोकारो डंपसाइट पर सात वर्षों की सेवा।",
      summary: "सबसे लंबे समय तक चलने वाली OED साझेदारियों में से एक — चास–बोकारो डंपसाइट पर निरंतर सेवा, सात वर्षों में गंध नियंत्रण व भूमि पुनर्प्राप्ति में सहयोग।",
    },
  },
  {
    slug: "reliance-bio-energy-biocng",
    city: "Reliance Bio-Energy Ltd",
    region: "Private Sector",
    tag: "Bio-CNG",
    headline: "Boosting biogas yield by 20–30% while cutting odour.",
    summary:
      "In Bio-CNG applications, dosing organic feedstock with OED at 1–2 litres per tonne consistently lifts biogas generation by 20–30% while neutralising process odour.",
    beforeImg: px("9090138"),
    afterImg: px("27490881"),
    metrics: [
      { k: "Biogas increase", v: "+20–30%", note: "observed" },
      { k: "Dosage", v: "1–2 L / MT", note: "of feedstock" },
      { k: "Microbial count", v: "4×10¹²", note: "microbes / ml" },
    ],
    challenge:
      "Bio-CNG operators needed higher biogas yields from the same feedstock while managing odour from organic handling.",
    approach:
      "OED's high-density microbial consortium was introduced into the feedstock at 1–2 litres per tonne to accelerate and enrich anaerobic digestion.",
    outcome:
      "A consistent 20–30% increase in biogas generation alongside cleaner, lower-odour operations.",
    quote:
      "More gas from the same feedstock, and a cleaner plant — the economics speak for themselves.",
    quoteBy: "Bio-Energy Operations",
    hi: {
      headline: "गंध घटाते हुए बायोगैस उपज में 20–30% की वृद्धि।",
      summary: "Bio-CNG अनुप्रयोगों में, जैविक फीडस्टॉक को 1–2 लीटर प्रति टन OED से उपचारित करने पर बायोगैस उत्पादन में लगातार 20–30% की वृद्धि होती है, साथ ही प्रक्रिया की गंध निष्क्रिय होती है।",
    },
  },
];

export const getCaseStudy = (slug) => caseStudies.find((c) => c.slug === slug);
